import classNames from 'classnames';
import type { LottieComponentProps } from 'lottie-react';
import * as React from 'react';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import type { LogoVariant } from '~/Partials/KristianiaLogo/LogoVariant';
import { loadLottieComponent } from '~/Partials/KristianiaLogo/utilsLottie.client';
import { FilledSvgIcon } from '~/Partials/SvgIcon/FilledSvgIcon';
import { assertNever } from '~/Partials/Utils/typeUtils';
import LogoHorizontalBlack from '~/Static/Images/AnimatedLogos/logo-horizontal-black.json';
import LogoSymbolBlack from '~/Static/Images/AnimatedLogos/logo-symbol-black.json';
import { LazyComponent } from '~/Utils/Lazy/LazyComponent';
import styles from './KristianiaLogo.module.css';

export interface KristianiaBaseLogoProps {
    label: string;
    variant?: LogoVariant;
    color?: 'white' | 'black';
}

export interface KristianiaPredefinedSizeLogoProps {
    size: 'medium' | 'large';
}

export interface KristianiaCustomSizeLogoProps {
    size: 'custom';
    width: number;
    height: number;
}

export interface KristianiaFixedLogoProps {
    type: 'fixed';
    version: 'horizontal' | 'symbol';
}

export interface KristianiaResponsiveLogoProps {
    type: 'responsive';
}

export type KristianiaLogoProps = KristianiaBaseLogoProps &
    (KristianiaFixedLogoProps | KristianiaResponsiveLogoProps) &
    (KristianiaPredefinedSizeLogoProps | KristianiaCustomSizeLogoProps);

export const KristianiaLogo: React.FC<KristianiaLogoProps> = props => {
    const { label, type, variant = 'default', color = 'black', size } = props;

    const resolveStyles = (): React.CSSProperties | undefined => {
        if (size === 'custom') {
            const { width, height } = props;

            return {
                '--logoWidth': `${width}px`,
                '--logoHeight': `${height}px`,
            } as React.CSSProperties;
        }

        return undefined;
    };

    const isColorful = variant === 'pride';

    const baseClassNames = classNames(
        styles.KristianiaLogo,
        size !== 'custom' && styles[`Variant--${size}`],
        !isColorful && styles[`Variant--${color}`]
    );

    const isResponsive = type === 'responsive';

    const getSymbolClasses = () => classNames(baseClassNames, styles['Variant--symbol'], isResponsive && 'mobileOnly');
    const getHorizontalClasses = () =>
        classNames(baseClassNames, styles['Variant--horizontal'], isResponsive && 'tabletAndUp');

    const shouldRenderSymbol = isResponsive || (type === 'fixed' && props.version === 'symbol');
    const shouldRenderHorizontal = isResponsive || (type === 'fixed' && props.version === 'horizontal');

    const getContent = () => {
        switch (variant) {
            case 'default':
                return (
                    <>
                        {shouldRenderSymbol && <SvgIcon iconName="logo-mobile" className={getSymbolClasses()} />}
                        {shouldRenderHorizontal && <SvgIcon iconName="base" className={getHorizontalClasses()} />}
                    </>
                );
            case 'pride':
                return (
                    <>
                        {shouldRenderSymbol && (
                            <FilledSvgIcon iconName="logo-mobile-pride" className={getSymbolClasses()} />
                        )}
                        {shouldRenderHorizontal && (
                            <FilledSvgIcon iconName="base-pride" className={getHorizontalClasses()} />
                        )}
                    </>
                );
            case 'animated':
                return (
                    <>
                        {shouldRenderSymbol && (
                            <LazyComponent<LottieComponentProps>
                                fallbackComponent={<SvgIcon iconName="logo-mobile" className={getSymbolClasses()} />}
                                componentProps={{
                                    animationData: LogoSymbolBlack,
                                    className: getSymbolClasses(),
                                }}
                                importComponentMethod={loadLottieComponent}
                            />
                        )}
                        {shouldRenderHorizontal && (
                            <LazyComponent<LottieComponentProps>
                                fallbackComponent={<SvgIcon iconName="base" className={getHorizontalClasses()} />}
                                componentProps={{
                                    animationData: LogoHorizontalBlack,
                                    className: getHorizontalClasses(),
                                }}
                                importComponentMethod={loadLottieComponent}
                            />
                        )}
                    </>
                );
            default:
                assertNever(variant);
        }
    };

    return (
        <span style={resolveStyles()}>
            <span className="visuallyHidden">{label}</span>
            {getContent()}
        </span>
    );
};
